import PhotoShare from "../components/PhotoShare";
import { photoSharePath } from "../lib/photoShare";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Award,
  BookImage,
  CalendarDays,
  Camera,
  Check,
  ChevronDown,
  Expand,
  FolderInput,
  ListOrdered,
  Lock,
  LockOpen,
  MapPin,
  MessageCircle,
  Pencil,
  Plus,
  Send,
  Trash2,
  Upload,
  UserRound,
  X,
} from "lucide-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import DeleteConfirmation from "../components/DeleteConfirmation";
import MemberGate from "../components/MemberGate";
import { ErrorMessage, Loading } from "../components/Loading";
import { useApi } from "../hooks/useApi";
import { api } from "../lib/api";

const emptyAlbum = {
  title: "",
  classLabel: "MACE 95",
  year: "",
  location: "",
  occasion: "",
  storyline: "",
};

function CreateCollegeAlbum({ canContribute, onClose, onCreated }) {
  const [form, setForm] = useState(emptyAlbum);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const dialog = useRef(null);

  useEffect(() => dialog.current?.focus(), []);

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const result = await api("/api/college-days/albums", {
        method: "POST",
        body: JSON.stringify(form),
      });
      await onCreated(result.album);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal-panel college-album-modal" role="dialog" aria-modal="true" aria-labelledby="create-college-album-title" tabIndex="-1" ref={dialog}>
        <button className="icon-button modal-close" type="button" onClick={onClose} aria-label="Close"><X aria-hidden="true" /></button>
        {!canContribute ? (
          <>
            <p className="kicker">Read-only member access</p>
            <h2 id="create-college-album-title">Use your individual account.</h2>
            <p className="modal-intro">The shared class passphrase is for viewing. Sign in with your personal account to create and curate an album.</p>
          </>
        ) : (
          <>
            <p className="kicker">Open a new chapter</p>
            <h2 id="create-college-album-title">Create a College Days album.</h2>
            <p className="modal-intro">Tell classmates which class these frames belong to, when they were made, and the place or occasion that brought everyone together.</p>
            <form className="form-grid" onSubmit={submit}>
              <label className="form-field form-field--wide">Album title
                <input required maxLength="120" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder="Civil ’95 industrial visit" autoFocus />
              </label>
              <label className="form-field">Class
                <input required maxLength="120" value={form.classLabel} onChange={(event) => setForm({ ...form, classLabel: event.target.value })} placeholder="Civil Engineering · MACE 95" />
              </label>
              <label className="form-field">Year
                <input required type="number" min="1900" max="2100" inputMode="numeric" value={form.year} onChange={(event) => setForm({ ...form, year: event.target.value })} placeholder="1994" />
              </label>
              <label className="form-field">Location
                <input maxLength="180" value={form.location} onChange={(event) => setForm({ ...form, location: event.target.value })} placeholder="Kothamangalam or tour destination" />
              </label>
              <label className="form-field">Occasion
                <input maxLength="180" value={form.occasion} onChange={(event) => setForm({ ...form, occasion: event.target.value })} placeholder="Arts festival, tour, hostel day…" />
              </label>
              <p className="college-album-form-note form-field--wide">Include at least a location or an occasion.</p>
              <label className="form-field form-field--wide">Opening storyline
                <textarea rows="5" maxLength="2000" value={form.storyline} onChange={(event) => setForm({ ...form, storyline: event.target.value })} placeholder="Set the scene. Anyone with an individual class account can help refine this story later." />
              </label>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              <button className="button button--coral" disabled={saving} type="submit">{saving ? "Creating…" : "Create album"} <ArrowRight aria-hidden="true" /></button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}

function NominationControl({ photo, canContribute, onReload, compact = false }) {
  const [result, setResult] = useState(null);
  const [nominating, setNominating] = useState(false);
  const nominationStatus = useRef(null);
  const [error, setError] = useState("");
  const count = Number(result?.nominationCount ?? photo.nominationCount ?? 0);
  const threshold = Number(result?.nominationThreshold ?? photo.nominationThreshold ?? 10);
  const nominated = Boolean(result?.nominatedByViewer ?? photo.nominatedByViewer);
  const promoted = Boolean(result?.promotedToBest ?? photo.promotedToBest);
  const progress = Math.min(100, Math.round((count / threshold) * 100));

  useEffect(() => setResult(null), [photo.id, photo.nominationCount, photo.nominatedByViewer]);

  async function nominate() {
    setNominating(true);
    setError("");
    try {
      const response = await api(`/api/college-days/${encodeURIComponent(photo.id)}/nominations`, { method: "POST" });
      setResult(response);
      await onReload?.();
      nominationStatus.current?.focus({ preventScroll: true });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setNominating(false);
    }
  }

  return (
    <section className={`${compact ? "college-days-nomination is-compact" : "college-days-nomination"}${promoted ? " is-selected" : ""}`} aria-label="Best of College Days nominations">
      <div className="college-days-nomination__heading">
        <span className="college-days-nomination__award"><Award aria-hidden="true" /></span>
        <div><p className="kicker">Best of College Days</p><strong>{count} {count === 1 ? "nomination" : "nominations"}</strong></div>
        <b>{count}<small>/ {threshold}</small></b>
      </div>
      <div className="college-days-nomination__track" role="progressbar" aria-label={`${count} of ${threshold} nominations`} aria-valuemin="0" aria-valuemax={threshold} aria-valuenow={Math.min(count, threshold)}>
        <span style={{ width: `${progress}%` }} />
      </div>
      {promoted
        ? <p className="college-days-nomination__status"><Award aria-hidden="true" /> Selected for the Best of College Days album.</p>
        : <p className="college-days-nomination__status">{threshold - count} more {threshold - count === 1 ? "nomination" : "nominations"} to enter the award album.</p>}
      {canContribute ? (
        nominated
          ? <span ref={nominationStatus} tabIndex={-1} className="college-days-nomination__counted"><Check aria-hidden="true" /> Your nomination is counted</span>
          : <button className="button button--yellow" type="button" onClick={nominate} disabled={nominating}>{nominating ? "Nominating…" : "Nominate this photograph"} <Award aria-hidden="true" /></button>
      ) : (
        <p className="college-days-nomination__account">Use a personal classmate account to nominate this photograph.</p>
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </section>
  );
}

function NominatorList({ photo }) {
  const [open, setOpen] = useState(false);
  const nominators = photo.nominators || [];
  if (!Number(photo.nominationCount)) return null;

  return (
    <section className={`college-days-comments college-days-nominators${open ? " is-open" : ""}`}>
      <button className="college-days-comments__toggle" type="button" aria-expanded={open} aria-controls={`college-days-nominators-${photo.id}`} onClick={() => setOpen((value) => !value)}>
        <span><UserRound aria-hidden="true" /> Nominated by {photo.nominationCount} {Number(photo.nominationCount) === 1 ? "classmate" : "classmates"}</span>
        <span>{open ? "Hide" : "View"} <ChevronDown aria-hidden="true" /></span>
      </button>
      {open && (
        <ul className="college-days-nominators__list" id={`college-days-nominators-${photo.id}`}>
          {nominators.map((person) => <li key={person.memberId}>{person.name}</li>)}
        </ul>
      )}
    </section>
  );
}

function ArchiveLightbox({ photo, albumId, canContribute, onReload, onClose }) {
  useEffect(() => {
    function closeOnEscape(event) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return (
    <div className="college-days-lightbox" role="dialog" aria-modal="true" aria-label="College Days photograph" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <button className="icon-button" type="button" onClick={onClose} aria-label="Close photograph"><X aria-hidden="true" /></button>
      <figure>
        <img src={photo.src} alt={photo.title || photo.description || photo.originalAlt} />
        <figcaption>{photo.title || photo.description || photo.originalAlt}</figcaption>
        <PhotoShare path={photoSharePath(`/college-days/albums/${encodeURIComponent(albumId)}`, albumId, photo.id)} title={photo.title || photo.originalAlt} />
        <NominationControl photo={photo} canContribute={canContribute} onReload={onReload} compact />
      </figure>
    </div>
  );
}

const commentDate = (value) => new Intl.DateTimeFormat("en", {
  month: "short",
  day: "numeric",
  year: "numeric",
}).format(new Date(value));

function ArchiveEntry({ photo, index, albumId, photoCount, canContribute, isAdministrator, canCurateCollegeDays, albums, onReload, onOpen }) {
  const [editing, setEditing] = useState(false);
  const editButton = useRef(null);
  const commentInput = useRef(null);
  const [title, setTitle] = useState(photo.title || "");
  const [description, setDescription] = useState(photo.description || "");
  const [location, setLocation] = useState(photo.location || "");
  const [year, setYear] = useState(photo.year || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [commenting, setCommenting] = useState(false);
  const [removingComment, setRemovingComment] = useState("");
  const [moving, setMoving] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [destinationAlbumId, setDestinationAlbumId] = useState("");
  const [showOrder, setShowOrder] = useState(false);
  const [targetPosition, setTargetPosition] = useState(index + 1);
  const [reordering, setReordering] = useState(false);
  const moveDestinations = albums.filter((album) => !album.curated && album.id !== photo.albumId);

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api(`/api/college-days/${encodeURIComponent(photo.id)}`, {
        method: "PATCH",
        body: JSON.stringify({ title, description, location, year: year || null }),
      });
      await onReload();
      setEditing(false);
      requestAnimationFrame(() => editButton.current?.focus({ preventScroll: true }));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    const directUpload = photo.sourceAlbumId === "college-days-uploads";
    const prompt = directUpload
      ? "Delete this photograph from College Days?"
      : `Return this photograph to ${photo.sourceAlbumTitle || "its original reunion album"}?`;
    if (!window.confirm(prompt)) return;
    setSaving(true);
    setError("");
    try {
      await api(`/api/admin/college-days/${encodeURIComponent(photo.id)}`, { method: "DELETE" });
      await onReload();
    } catch (requestError) {
      setError(requestError.message);
      setSaving(false);
    }
  }

  async function addComment(event) {
    event.preventDefault();
    setCommenting(true);
    setError("");
    try {
      await api(`/api/college-days/${encodeURIComponent(photo.id)}/comments`, {
        method: "POST",
        body: JSON.stringify({ body: comment }),
      });
      await onReload();
      setComment("");
      commentInput.current?.focus({ preventScroll: true });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setCommenting(false);
    }
  }

  async function removeComment(item) {
    if (!window.confirm("Remove this comment?")) return;
    setRemovingComment(item.id);
    setError("");
    try {
      await api(`/api/college-days/${encodeURIComponent(photo.id)}/comments/${item.id}`, { method: "DELETE" });
      await onReload();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setRemovingComment("");
    }
  }

  async function moveToAlbum(event) {
    event.preventDefault();
    if (!destinationAlbumId) return;
    setMoving(true);
    setError("");
    try {
      await api(`/api/admin/college-days/${encodeURIComponent(photo.id)}/album`, {
        method: "PATCH",
        body: JSON.stringify({ albumId: destinationAlbumId }),
      });
      await onReload();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setMoving(false);
    }
  }

  async function reorderPhoto(event) {
    event.preventDefault();
    const position = Number(targetPosition);
    if (!Number.isInteger(position) || position < 1 || position > photoCount) {
      setError(`Choose a position between 1 and ${photoCount}.`);
      return;
    }
    if (position === index + 1) {
      setShowOrder(false);
      return;
    }
    setReordering(true);
    setError("");
    try {
      await api(`/api/admin/college-days/albums/${encodeURIComponent(albumId)}/photo-order`, {
        method: "PATCH",
        body: JSON.stringify({ photoId: photo.id, targetPosition: position }),
      });
      setShowOrder(false);
      await onReload();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setReordering(false);
    }
  }

  return (
    <article id={`photo-${photo.id}`} tabIndex={-1} className="college-days-entry" style={{ "--archive-index": index }}>
      <div className="college-days-entry__number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</div>
      <div className="college-days-entry__visual">
        <button className="college-days-entry__image" type="button" onClick={() => onOpen(photo)} aria-label={`Open photograph ${index + 1}`}>
          <img src={photo.src} alt={photo.title || photo.description || photo.originalAlt} loading={index > 1 ? "lazy" : "eager"} />
          <span><Expand aria-hidden="true" /> View full frame</span>
        </button>
        <PhotoShare path={photoSharePath(`/college-days/albums/${encodeURIComponent(albumId)}`, albumId, photo.id)} title={photo.title || photo.originalAlt} />
      </div>
      <div className="college-days-entry__story">
        <p className="kicker">From {photo.sourceAlbumTitle || "the class archive"}</p>
        {editing ? (
          <form className="college-days-note-form" onSubmit={save}>
            <div className="college-days-note-form__metadata">
              <label className="form-field">Photograph title
                <input maxLength="160" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Give this frame a memorable title" autoFocus />
              </label>
              <label className="form-field">Location
                <input maxLength="180" value={location} onChange={(event) => setLocation(event.target.value)} placeholder="Campus, city, or venue" />
              </label>
              <label className="form-field">Year
                <input type="number" min="1900" max="2100" inputMode="numeric" value={year} onChange={(event) => setYear(event.target.value)} placeholder="1995" />
              </label>
            </div>
            <label className="form-field">Photograph description
              <textarea rows="7" maxLength="1200" value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Who is here? Include their names, what was happening, and what you remember." />
              <small>{description.length} / 1200 characters</small>
            </label>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <div className="button-row">
              <button className="button button--ink" type="submit" disabled={saving}>{saving ? "Saving…" : "Save description"} <Check aria-hidden="true" /></button>
              <button className="button button--ghost" type="button" onClick={() => { setTitle(photo.title || ""); setDescription(photo.description || ""); setLocation(photo.location || ""); setYear(photo.year || ""); setEditing(false); setError(""); }} disabled={saving}>Cancel</button>
            </div>
          </form>
        ) : (
          <>
            <h3>{photo.title || photo.originalAlt || "Untitled photograph"}</h3>
            {photo.description && <blockquote>{photo.description}</blockquote>}
            {(photo.location || photo.year) && <p className="college-days-entry__place">{[photo.location, photo.year].filter(Boolean).join(" · ")}</p>}
            <p className="college-days-entry__date">Published {new Date(photo.createdAt).toLocaleDateString("en", { year: "numeric", month: "long", day: "numeric" })}</p>
            {(canContribute || isAdministrator) && (
              <div className="college-days-entry__admin">
                {canContribute && <button ref={editButton} className="text-link" type="button" onClick={() => setEditing(true)}><Pencil aria-hidden="true" /> Edit story &amp; context</button>}
                {isAdministrator && photoCount > 1 && <button className="text-link" type="button" aria-expanded={showOrder} aria-controls={`college-days-order-${photo.id}`} onClick={() => { setTargetPosition(index + 1); setError(""); setShowMove(false); setShowOrder((value) => !value); }}><ListOrdered aria-hidden="true" /> Change order</button>}
                {canCurateCollegeDays && moveDestinations.length > 0 && <button className="text-link" type="button" onClick={() => { setShowOrder(false); setShowMove((value) => !value); }}><FolderInput aria-hidden="true" /> Move to another album</button>}
                {isAdministrator && <button className="text-link is-danger" type="button" onClick={remove} disabled={saving}><Trash2 aria-hidden="true" /> {photo.sourceAlbumId === "college-days-uploads" ? "Delete photograph" : "Return to reunion album"}</button>}
              </div>
            )}
            {showOrder && isAdministrator && (
              <form className="college-days-order-form" id={`college-days-order-${photo.id}`} onSubmit={reorderPhoto}>
                <div>
                  <ListOrdered aria-hidden="true" />
                  <span><b>Move this photograph</b><small>Currently {index + 1} of {photoCount}</small></span>
                </div>
                <label htmlFor={`college-days-position-${photo.id}`}>New position</label>
                <input id={`college-days-position-${photo.id}`} type="number" min="1" max={photoCount} required value={targetPosition} onChange={(event) => setTargetPosition(event.target.value)} />
                <span className="college-days-order-form__total">of {photoCount}</span>
                <button className="button button--small button--ink" type="submit" disabled={reordering || !Number.isInteger(Number(targetPosition)) || Number(targetPosition) < 1 || Number(targetPosition) > photoCount || Number(targetPosition) === index + 1}>{reordering ? "Saving…" : "Save order"}</button>
                <button className="text-link" type="button" onClick={() => { setShowOrder(false); setError(""); }} disabled={reordering}>Cancel</button>
              </form>
            )}
            {showMove && canCurateCollegeDays && (
              <form className="college-days-move-form" onSubmit={moveToAlbum}>
                <label htmlFor={`college-days-move-${photo.id}`}>Destination album</label>
                <select id={`college-days-move-${photo.id}`} required value={destinationAlbumId} onChange={(event) => setDestinationAlbumId(event.target.value)}>
                  <option value="">Choose an album</option>
                  {moveDestinations.map((album) => <option key={album.id} value={album.id} disabled={!album.uploadsOpen}>{album.title}{album.uploadsOpen ? "" : " — additions closed"}</option>)}
                </select>
                <button className="button button--small button--ink" type="submit" disabled={moving || !destinationAlbumId}>{moving ? "Moving…" : "Move photograph"} <ArrowRight aria-hidden="true" /></button>
                <button className="text-link" type="button" onClick={() => { setShowMove(false); setDestinationAlbumId(""); setError(""); }} disabled={moving}>Cancel</button>
              </form>
            )}
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </>
        )}

        <NominationControl photo={photo} canContribute={canContribute} onReload={onReload} />
        <NominatorList photo={photo} />

        <section className={commentsOpen ? "college-days-comments is-open" : "college-days-comments"}>
          <button className="college-days-comments__toggle" type="button" aria-expanded={commentsOpen} aria-controls={`college-days-comments-${photo.id}`} onClick={() => setCommentsOpen((value) => !value)}>
            <span><MessageCircle aria-hidden="true" /> {photo.comments?.length || 0} {(photo.comments?.length || 0) === 1 ? "comment" : "comments"}</span>
            <span>{commentsOpen ? "Hide" : "View"} <ChevronDown aria-hidden="true" /></span>
          </button>
          {commentsOpen && (
            <div className="college-days-comments__panel" id={`college-days-comments-${photo.id}`}>
              {canContribute && (
                <form className="college-days-comment-form" onSubmit={addComment}>
                  <label htmlFor={`college-days-comment-${photo.id}`}>Add what this photograph brings back</label>
                  <textarea ref={commentInput} id={`college-days-comment-${photo.id}`} required maxLength="1200" rows="3" value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Share a detail, a name, or a memory…" />
                  <div>
                    <small>{comment.length} / 1,200</small>
                    <button className="button button--small button--ink" type="submit" disabled={commenting || !comment.trim()}>{commenting ? "Posting…" : "Post comment"} <Send aria-hidden="true" /></button>
                  </div>
                </form>
              )}
              {(photo.comments?.length || 0) === 0 ? (
                <p className="college-days-comments__empty">No comments yet.</p>
              ) : (
                <div className="college-days-comment-list">
                  {photo.comments.map((item) => (
                    <article className="college-days-comment" key={item.id}>
                      <p>{item.body}</p>
                      <footer>
                        <span><strong>{item.author.name}</strong> · {commentDate(item.createdAt)}</span>
                        {item.canDelete && <button type="button" onClick={() => removeComment(item)} disabled={removingComment === item.id}><Trash2 aria-hidden="true" /> {removingComment === item.id ? "Removing…" : "Remove"}</button>}
                      </footer>
                    </article>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </article>
  );
}

function AlbumEditor({ album, onCancel, onSaved }) {
  const [form, setForm] = useState({
    title: album.title,
    classLabel: album.classLabel,
    year: album.year,
    location: album.location,
    occasion: album.occasion,
    storyline: album.storyline,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api(`/api/college-days/albums/${encodeURIComponent(album.id)}`, {
        method: "PATCH",
        body: JSON.stringify(form),
      });
      await onSaved();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="college-album-editor" onSubmit={submit}>
      <div className="college-album-editor__metadata">
        <label className="form-field">Album title<input required maxLength="120" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} /></label>
        <label className="form-field">Class<input required maxLength="120" value={form.classLabel} onChange={(event) => setForm({ ...form, classLabel: event.target.value })} /></label>
        <label className="form-field">Year<input required maxLength="9" value={form.year} onChange={(event) => setForm({ ...form, year: event.target.value })} /></label>
        <label className="form-field">Location<input maxLength="180" value={form.location} onChange={(event) => setForm({ ...form, location: event.target.value })} /></label>
        <label className="form-field">Occasion<input maxLength="180" value={form.occasion} onChange={(event) => setForm({ ...form, occasion: event.target.value })} /></label>
      </div>
      <label className="form-field">Album storyline
        <textarea rows="7" maxLength="2000" value={form.storyline} onChange={(event) => setForm({ ...form, storyline: event.target.value })} placeholder="Set the scene for these photographs." />
        <small>{form.storyline.length} / 2,000 characters</small>
      </label>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <div className="button-row">
        <button className="button button--yellow" type="submit" disabled={saving}>{saving ? "Saving…" : "Save album story"} <Check aria-hidden="true" /></button>
        <button className="button button--ghost" type="button" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </form>
  );
}

function AlbumDirectory({ albums, canContribute, onCreate }) {
  return (
    <section className="college-album-directory section-wrap" aria-label="College Days albums">
      <div className="college-days-archive__heading">
        <div><p className="kicker">Albums by class, place, and occasion</p><h2>Every collection opens another door.</h2></div>
        <p><BookImage aria-hidden="true" /> The original photographs live together in the archive album. New albums can hold a class, a tour, a festival, or any shared chapter.</p>
      </div>
      <div className="college-album-grid">
        {albums.map((album, index) => (
          <article className={album.curated ? "college-album-card is-curated" : "college-album-card"} key={album.id} style={{ "--album-index": index }}>
            <Link className="college-album-card__cover" to={`/college-days/albums/${album.id}`}>
              {album.cover
                ? <img src={album.cover} alt="" />
                : album.curated
                  ? <img src="/media/brand/mace-front-legacy.webp" alt="" />
                  : <span><Camera aria-hidden="true" /> First frame waiting</span>}
              {album.curated && <span className="college-album-card__award"><Award aria-hidden="true" /> Classmates’ choice</span>}
              <b>{String(index + 1).padStart(2, "0")}</b>
            </Link>
            <div className="college-album-card__copy">
              <p className="kicker">{album.classLabel} · {album.year}</p>
              <h3><Link to={`/college-days/albums/${album.id}`}>{album.title}</Link></h3>
              {album.curated && <p className="college-album-card__rule"><Award aria-hidden="true" /> Ten distinct nominations earn a place in this album.</p>}
              {album.createdBy && <p className="college-album-card__creator"><UserRound aria-hidden="true" /> Created by <strong>{album.createdBy.name}</strong></p>}
              {album.storyline && <p>{album.storyline}</p>}
              <div className="college-album-card__facts">
                {album.location && <span><MapPin aria-hidden="true" /> {album.location}</span>}
                {album.occasion && <span><CalendarDays aria-hidden="true" /> {album.occasion}</span>}
              </div>
              <Link className="text-link" to={`/college-days/albums/${album.id}`}>{album.photoCount} {album.photoCount === 1 ? "frame" : "frames"} · Open album <ArrowRight aria-hidden="true" /></Link>
            </div>
          </article>
        ))}
      </div>
      {canContribute && (
        <div className="college-album-cta">
          <p className="kicker">Another chapter to remember?</p>
          <h2>Give those photographs a place of their own.</h2>
          <button className="button button--ink" type="button" onClick={onCreate}><Plus aria-hidden="true" /> Create a College Days album</button>
        </div>
      )}
    </section>
  );
}

function BestOfCandidates({ photos, albums }) {
  const albumTitles = new Map(albums.map((album) => [album.id, album.title]));

  return (
    <section className="best-of-candidates" aria-labelledby="best-of-candidates-title">
      <header className="best-of-candidates__header">
        <div className="best-of-candidates__intro">
          <span><Award aria-hidden="true" /></span>
          <div>
            <p className="kicker">Help choose what enters next</p>
            <h2 id="best-of-candidates-title">These photographs already have a vote.</h2>
          </div>
        </div>
        <p>Open a stamp in its original album and add your nomination. Ten classmates’ votes publish it here.</p>
      </header>
      <div className="best-of-candidates__grid">
        {photos.map((photo) => {
          const title = photo.title || photo.originalAlt || "College Days photograph";
          const albumTitle = albumTitles.get(photo.albumId) || photo.sourceAlbumTitle || "College Days Archive";
          const voteCount = Number(photo.nominationCount || 0);
          return (
            <Link
              className="best-of-candidate"
              key={photo.id}
              to={`/college-days/albums/${encodeURIComponent(photo.albumId)}?photo=${encodeURIComponent(photo.id)}`}
              aria-label={`Open “${title}” in ${albumTitle} and vote for it`}
            >
              <span className="best-of-candidate__stamp">
                <img src={photo.src} alt="" loading="lazy" />
                <b>{voteCount} {voteCount === 1 ? "vote" : "votes"}</b>
              </span>
              <strong>{title}</strong>
              <small>From {albumTitle}</small>
              <span className="best-of-candidate__cta">Open original album &amp; vote <ArrowRight aria-hidden="true" /></span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

export default function CollegeDays({ session, onLogin, isAdministrator, canCurateCollegeDays }) {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authenticated = Boolean(session?.authenticated);
  const canContribute = Number(session?.user?.memberId) > 0;
  const loader = useCallback(
    () => authenticated ? api("/api/college-days") : Promise.resolve({ albums: [], photos: [] }),
    [authenticated],
  );
  const { data, error, loading, refresh: reload } = useApi(loader, [loader]);
  const [openPhoto, setOpenPhoto] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [changingUploads, setChangingUploads] = useState(false);
  const [showDeleteAlbum, setShowDeleteAlbum] = useState(false);
  const [deletingAlbum, setDeletingAlbum] = useState(false);
  const [deleteAlbumError, setDeleteAlbumError] = useState("");
  const albums = data?.albums || [];
  const activeAlbum = useMemo(() => albums.find((album) => album.id === albumId), [albumId, albums]);
  const totalPhotos = data?.photos?.length || albums.reduce((sum, album) => sum + album.photoCount, 0);
  const requestedPhotoId = searchParams.get("photo") || "";
  const bestOfCandidates = useMemo(() => (data?.photos || [])
    .filter((photo) => {
      const count = Number(photo.nominationCount || 0);
      const threshold = Number(photo.nominationThreshold || 10);
      return count >= 1 && count < threshold;
    })
    .sort((left, right) => Number(right.nominationCount) - Number(left.nominationCount) ||
      String(right.updatedAt || right.createdAt).localeCompare(String(left.updatedAt || left.createdAt))), [data?.photos]);

  const linkedPhotoExists = Boolean(activeAlbum?.photos?.some((photo) => String(photo.id) === requestedPhotoId));
  useEffect(() => {
    if (loading || !linkedPhotoExists) return;
    const frame = requestAnimationFrame(() => {
      const entry = document.getElementById(`photo-${requestedPhotoId}`);
      entry?.scrollIntoView({ block: "start", behavior: "instant" });
      entry?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [albumId, requestedPhotoId, linkedPhotoExists, loading]);

  async function refreshPhoto(photoId) {
    const entry = document.getElementById(`photo-${photoId}`);
    const top = entry?.getBoundingClientRect().top;
    await reload();
    await new Promise((resolve) => requestAnimationFrame(resolve));
    if (entry?.isConnected && top !== undefined) {
      const shift = entry.getBoundingClientRect().top - top;
      if (shift) window.scrollBy({ top: shift, behavior: "instant" });
    }
  }

  function closeOpenPhoto() {
    setOpenPhoto(null);
  }

  async function uploadPhotos(event) {
    const files = [...(event.target.files || [])];
    if (!files.length || !activeAlbum) return;
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    if (files.length > 8 || totalBytes > 24 * 1024 * 1024 || files.some((file) => file.size > 8 * 1024 * 1024)) {
      setUploadError("Choose up to 8 photographs, each no larger than 8 MB and totaling no more than 24 MB.");
      event.target.value = "";
      return;
    }
    setUploading(true);
    setUploadError("");
    const body = new FormData();
    files.forEach((file) => body.append("photos", file));
    try {
      await api(`/api/college-days/albums/${encodeURIComponent(activeAlbum.id)}/photos`, { method: "POST", body });
      await reload();
    } catch (requestError) {
      setUploadError(requestError.message);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }

  async function toggleAlbumUploads() {
    if (!activeAlbum) return;
    setChangingUploads(true);
    setUploadError("");
    try {
      await api(`/api/college-days/albums/${encodeURIComponent(activeAlbum.id)}/uploads`, {
        method: "PATCH",
        body: JSON.stringify({ uploadsOpen: !activeAlbum.uploadsOpen }),
      });
      await reload();
    } catch (requestError) {
      setUploadError(requestError.message);
    } finally {
      setChangingUploads(false);
    }
  }

  async function deleteAlbum() {
    if (!activeAlbum) return;
    setDeletingAlbum(true);
    setDeleteAlbumError("");
    try {
      await api(`/api/admin/college-days/albums/${encodeURIComponent(activeAlbum.id)}`, {
        method: "DELETE",
      });
      setShowDeleteAlbum(false);
      navigate("/college-days");
      await reload();
    } catch (requestError) {
      setDeleteAlbumError(requestError.message);
      setDeletingAlbum(false);
    }
  }

  if (!authenticated) {
    return <div className="college-days-gate section-wrap"><MemberGate onSuccess={onLogin} /></div>;
  }

  if (albumId) {
    if (loading) return <div className="section-wrap"><Loading label="Opening the College Days album" /></div>;
    if (error) return <div className="section-wrap"><ErrorMessage>{error}</ErrorMessage></div>;
    if (!activeAlbum) return <div className="college-days-missing section-wrap"><ErrorMessage>That College Days album could not be found.</ErrorMessage><Link className="button button--ink" to="/college-days"><ArrowLeft aria-hidden="true" /> All College Days albums</Link></div>;
    const photos = activeAlbum.photos || [];
    const isBestOf = Boolean(activeAlbum.curated);
    return (
      <>
        <section className="college-album-hero">
          <div className="college-album-hero__inner section-wrap">
            <div className="college-album-hero__copy">
              <Link className="back-link" to="/college-days"><ArrowLeft aria-hidden="true" /> All College Days albums</Link>
              <p className="kicker">{activeAlbum.classLabel} · {activeAlbum.year}</p>
              {editingAlbum && !isBestOf ? (
                <AlbumEditor album={activeAlbum} onCancel={() => setEditingAlbum(false)} onSaved={async () => { await reload(); setEditingAlbum(false); }} />
              ) : (
                <>
                  <h1>{activeAlbum.title}</h1>
                  <div className="college-album-hero__facts">
                    {activeAlbum.location && <span><MapPin aria-hidden="true" /> {activeAlbum.location}</span>}
                    {activeAlbum.occasion && <span><CalendarDays aria-hidden="true" /> {activeAlbum.occasion}</span>}
                    <span><Camera aria-hidden="true" /> {photos.length} {photos.length === 1 ? "photograph" : "photographs"}</span>
                  </div>
                  {activeAlbum.storyline && <p className="college-album-hero__storyline">{activeAlbum.storyline}</p>}
                  {activeAlbum.createdBy && <p className="college-album-hero__creator"><UserRound aria-hidden="true" /> Album created by <strong>{activeAlbum.createdBy.name}</strong></p>}
                  {!isBestOf && <p className={activeAlbum.uploadsOpen ? "college-album-availability is-open" : "college-album-availability is-closed"}>{activeAlbum.uploadsOpen ? <LockOpen aria-hidden="true" /> : <Lock aria-hidden="true" />} {activeAlbum.uploadsOpen ? "Open to new photographs and additions" : "Closed to new photographs and additions"}</p>}
                  {canContribute && !isBestOf && (
                    <div className="college-album-hero__actions">
                      <button className="button button--cream" type="button" onClick={() => setEditingAlbum(true)}><Pencil aria-hidden="true" /> Edit album &amp; storyline</button>
                      {activeAlbum.uploadsOpen && <label className="button button--yellow college-days-upload">
                        <Upload aria-hidden="true" /> {uploading ? "Adding photographs…" : "Add photographs"}
                        <input type="file" accept="image/jpeg,image/png,image/webp" multiple disabled={uploading} onChange={uploadPhotos} />
                      </label>}
                      {activeAlbum.canManageUploads && <button className="button button--ghost college-album-upload-toggle" type="button" onClick={toggleAlbumUploads} disabled={changingUploads}>{activeAlbum.uploadsOpen ? <Lock aria-hidden="true" /> : <LockOpen aria-hidden="true" />} {changingUploads ? "Updating…" : activeAlbum.uploadsOpen ? "Close album to additions" : "Reopen album to additions"}</button>}
                      {canCurateCollegeDays && activeAlbum.id !== "college-days-archive" && <button className="button button--danger" type="button" onClick={() => { setDeleteAlbumError(""); setShowDeleteAlbum(true); }}><Trash2 aria-hidden="true" /> Delete album</button>}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="college-album-hero__image" aria-hidden="true">
              {activeAlbum.cover
                ? <img src={activeAlbum.cover} alt="" />
                : isBestOf
                  ? <img src="/media/brand/mace-front-legacy.webp" alt="" />
                  : <BookImage />}
              <span>{isBestOf ? "10 votes" : activeAlbum.year}</span>
            </div>
          </div>
        </section>

        <section className="college-days-archive section-wrap" aria-live="polite">
          <div className="college-days-archive__heading">
            <div><p className="kicker">{isBestOf ? "The classmates’ award album" : "The album, one frame at a time"}</p><h2>{isBestOf ? "Ten voices turn a memory into a class favorite." : "Names, places, and memories belong here."}</h2></div>
            <p>{isBestOf ? <><Award aria-hidden="true" /> Photographs appear here automatically after ten distinct classmates nominate them.</> : <><BookImage aria-hidden="true" /> Anyone using an individual class account can add context, improve the storyline, and join the conversation around a photograph.</>}</p>
          </div>
          {uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}
          {isBestOf && bestOfCandidates.length > 0 && <BestOfCandidates photos={bestOfCandidates} albums={albums} />}
          {photos.length === 0 && (
            <div className="college-days-empty">
              {isBestOf ? <Award aria-hidden="true" /> : <BookImage aria-hidden="true" />}
              <p className="kicker">{isBestOf ? "The nominations are underway" : "The first page is waiting"}</p>
              <h2>{isBestOf ? "The first class favorite is waiting for ten votes." : "Bring this album into view."}</h2>
              <p>{isBestOf ? "Open any College Days album and nominate the photographs you believe belong here." : !activeAlbum.uploadsOpen ? "This album is not accepting new photographs or additions right now." : canContribute ? "Add the first photographs, then invite classmates to fill in the names and memories." : "Classmates are gathering photographs for this album. Check back as the story takes shape."}</p>
              {canContribute && !isBestOf && activeAlbum.uploadsOpen && (
                <label className="button button--ink college-days-upload">
                  <Upload aria-hidden="true" /> {uploading ? "Adding photographs…" : "Add the first photographs"}
                  <input type="file" accept="image/jpeg,image/png,image/webp" multiple disabled={uploading} onChange={uploadPhotos} />
                </label>
              )}
            </div>
          )}
          {photos.map((photo, index) => <ArchiveEntry key={photo.id} photo={photo} index={index} albumId={activeAlbum.id} photoCount={photos.length} canContribute={canContribute} isAdministrator={isAdministrator} canCurateCollegeDays={canCurateCollegeDays} albums={albums} onReload={() => refreshPhoto(photo.id)} onOpen={setOpenPhoto} />)}
        </section>
        {openPhoto && <ArchiveLightbox photo={data?.photos?.find((photo) => photo.id === openPhoto.id) || openPhoto} albumId={activeAlbum.id} canContribute={canContribute} onReload={() => refreshPhoto(openPhoto.id)} onClose={closeOpenPhoto} />}
        {showDeleteAlbum && (
          <DeleteConfirmation
            kicker="Delete album"
            title={`Delete “${activeAlbum.title}”?`}
            description={`The album will be removed. Its ${photos.length} ${photos.length === 1 ? "photograph" : "photographs"} will return to College Days Archive, with comments and nominations preserved.`}
            confirmLabel="Delete album"
            busyLabel="Deleting album…"
            busy={deletingAlbum}
            error={deleteAlbumError}
            onCancel={() => { setShowDeleteAlbum(false); setDeleteAlbumError(""); }}
            onConfirm={deleteAlbum}
          />
        )}
      </>
    );
  }

  return (
    <>
      <section className="college-days-hero">
        <div className="college-days-hero__years" aria-hidden="true"><span>1991</span><i>—</i><span>95</span></div>
        <div className="college-days-hero__copy section-wrap">
          <p className="kicker">The photographs before the reunions</p>
          <h1>College<br /><em>Days.</em></h1>
          <p>Classrooms, corridors, tours, hostels, festivals, and the unplanned frames that became part of our shared history.</p>
          <div className="college-days-hero__actions">
            <span>{albums.length} {albums.length === 1 ? "album" : "albums"} · {totalPhotos} {totalPhotos === 1 ? "photograph" : "photographs"}</span>
            {canContribute && <button className="button button--yellow" type="button" onClick={() => setShowCreate(true)}><Plus aria-hidden="true" /> Create an album</button>}
          </div>
        </div>
      </section>

      {loading && <div className="section-wrap college-days-loading"><Loading label="Opening the College Days archive" /></div>}
      {error && <div className="section-wrap"><ErrorMessage>{error}</ErrorMessage></div>}
      {!loading && !error && <AlbumDirectory albums={albums} canContribute={canContribute} onCreate={() => setShowCreate(true)} />}

      {showCreate && (
        <CreateCollegeAlbum
          canContribute={canContribute}
          onClose={() => setShowCreate(false)}
          onCreated={async (album) => {
            await reload();
            setShowCreate(false);
            navigate(`/college-days/albums/${album.id}`);
          }}
        />
      )}
    </>
  );
}
